import { useEffect, useState } from "react";
import { useLocation, useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, MessageCircle, Copy, XCircle, Loader2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useSettings } from "@/lib/hooks";
import { api, errMsg, tl, waLink } from "@/lib/api";

function usePaymentStatus(no, active) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    if (!active) return undefined;
    let n = 0;
    const tick = () => api.get(`/payments/status/${no}`).then((r) => setSt(r.data)).catch(() => {});
    tick();
    const t = setInterval(() => {
      n += 1;
      if (n > 40) clearInterval(t);
      tick();
    }, 3000);
    return () => clearInterval(t);
  }, [no, active]);
  return st;
}

function CardPayment({ no, order, st }) {
  const [url, setUrl] = useState(order?.iframe || null);
  const [busy, setBusy] = useState(false);
  const paid = st?.payment_status === "Ödendi";
  if (paid || st?.payment_status === "Başarısız" || st?.payment_status === "Ödendi (TEST)") return null;
  const retry = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/start", { order_no: no });
      if (data.type === "redirect") return window.location.assign(data.url);
      setUrl(data.url);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (url)
    return <iframe data-testid="paytr-iframe" title="Güvenli ödeme" src={url} className="w-full min-h-[640px] border-0 mt-6 rounded-lg" />;
  if (st?.payment_method !== "card" || st?.payment_status !== "Bekliyor") return null;
  return (
    <button data-testid="order-pay-now-button" onClick={retry} disabled={busy} className="mt-6 h-12 px-6 rounded-md bg-[#16A34A] text-white font-bold inline-flex items-center gap-2 disabled:opacity-60">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-5 w-5" />} Kartla Ödemeye Geç
    </button>
  );
}

function StatusBanner({ st, flag }) {
  if (!st || st.payment_method !== "card") return null;
  if (st.payment_status === "Ödendi (TEST)") return null;
  if (st.payment_status === "Ödendi")
    return <div data-testid="payment-status-ok" className="mt-4 rounded-md bg-green-50 border border-green-200 text-green-800 p-3 font-semibold">Ödemeniz alındı, siparişiniz hazırlanıyor.</div>;
  if (st.payment_status === "Başarısız" || flag === "hata")
    return <div data-testid="payment-status-fail" className="mt-4 rounded-md bg-red-50 border border-red-200 text-red-800 p-3 font-semibold flex items-center gap-2 justify-center"><XCircle className="h-5 w-5" />Ödeme tamamlanamadı. Kartınızdan para çekilmedi.</div>;
  if (flag)
    return <div data-testid="payment-status-wait" className="mt-4 rounded-md bg-blue-50 border border-blue-100 text-[#172554] p-3 font-semibold flex items-center gap-2 justify-center"><Loader2 className="h-4 w-4 animate-spin" />Ödemeniz kontrol ediliyor...</div>;
  return null;
}

export default function OrderDone() {
  const { no } = useParams();
  const order = useLocation().state;
  const [params] = useSearchParams();
  const flag = params.get("odeme");
  const s = useSettings();
  const st = usePaymentStatus(no, !!flag || order?.payment_method === "card" || !order);
  const method = order?.payment_method || st?.payment_method;
  const text = order
    ? `Merhaba, ${no} numaralı siparişim:\n${order.items.map((i) => `- ${i.qty} x ${i.name}`).join("\n")}\nToplam: ${tl(order.total)}\nÖdeme: ${order.payment_label}`
    : `Merhaba, ${no} numaralı siparişim hakkında yazıyorum.`;
  const failed = st?.payment_status === "Başarısız" || (flag === "hata" && st?.payment_method === "card" && st?.payment_status === "Bekliyor");
  return (
    <div className="max-w-2xl mx-auto px-4 py-14">
      <div className="bg-white border rounded-xl p-6 sm:p-10 text-center">
        {failed ? <XCircle className="h-16 w-16 text-red-500 mx-auto" /> : <CheckCircle2 className="h-16 w-16 text-[#16A34A] mx-auto" />}
        <h1 className="text-3xl sm:text-4xl font-bold text-[#172554] mt-4">{failed ? "Ödeme başarısız" : "Siparişiniz alındı"}</h1>
        <p className="mt-2 text-slate-600">Sipariş numaranız</p>
        <button data-testid="order-number" onClick={() => { navigator.clipboard?.writeText(no); toast.success("Kopyalandı"); }}
          className="mt-1 inline-flex items-center gap-2 font-mono text-2xl font-bold text-slate-900">{no} <Copy className="h-4 w-4 text-slate-400" /></button>
        {(order || st) && <p className="mt-3 font-semibold">Toplam: {tl(order?.total ?? st?.total)}{order ? ` · ${order.payment_label}` : ""}</p>}
        <StatusBanner st={st} flag={flag} />
        {method === "card" && <CardPayment no={no} order={order} st={st} />}
        {method === "eft" && (
          <div className="mt-6 text-left space-y-2" data-testid="order-iban-list">
            <p className="text-sm font-semibold">Lütfen ödemeyi aşağıdaki hesaplardan birine yapın ve açıklamaya <b>{no}</b> yazın:</p>
            {(s?.ibans || []).map((b) => (
              <div key={b.iban} className="border rounded-md p-3 bg-slate-50 text-sm"><b>{b.bank}</b> · {b.holder}<div className="font-mono mt-1">{b.iban}</div></div>
            ))}
          </div>
        )}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          {s?.whatsapp_number && (
            <a data-testid="order-whatsapp-button" href={waLink(s.whatsapp_number, text)} target="_blank" rel="noreferrer"
              className="h-12 px-6 rounded-md bg-[#25D366] text-white font-bold flex items-center justify-center gap-2">
              <MessageCircle className="h-5 w-5" /> {method === "eft" ? "Dekontu WhatsApp'tan Gönder" : "WhatsApp'tan Bildir"}
            </a>
          )}
          <Link to="/" data-testid="order-continue-button" className="h-12 px-6 rounded-md bg-[#172554] text-white font-bold flex items-center justify-center">Alışverişe Devam</Link>
        </div>
      </div>
    </div>
  );
}
